import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Plus,
  Calendar,
  Video,
  FileText,
  RefreshCw,
} from "lucide-react";
import { useUserProfile } from "@/hooks/queries/useUser";
import { useSpecificSlot } from "@/hooks/queries/useSlots";
import { convertUTCToLocalTime, formatDateForDisplay } from "../../utils/timeUtils";
import { UserVideos } from "./components/Videos";
import { SessionDetailsTab } from "./components/Session";
import useAuth from "@/hooks/stores/useAuth";
import useUserDashboard from "@/hooks/stores/useUserDashboard";

export default function UserDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeTab, setActiveTab } = useUserDashboard();
  const [copiedLink, setCopiedLink] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const { data: userProfile, isLoading, error } = useUserProfile();
  const {
    data: specificSlot,
    isLoading: isSlotLoading,
    error: slotError,
    refetch,
  } = useSpecificSlot();

  // Get user flow type from auth store
  const { userFlow, isAuthenticated, userId } = useAuth();

  // Track if we've already triggered initial refetch for this session
  const hasTriggeredInitialRefetch = useRef(false);

  // Fallback: Ensure specific slot data is fetched on initial dashboard load after login
  // This only runs once per session, not on every page switch
  useEffect(() => {
    if (isAuthenticated && userId && !hasTriggeredInitialRefetch.current && !isSlotLoading) {
      hasTriggeredInitialRefetch.current = true;
      refetch();
    }
  }, [isAuthenticated, userId, isSlotLoading, refetch]);

  // Reset the refetch flag when user logs out so it can trigger again on next login
  useEffect(() => {
    if (!isAuthenticated) {
      hasTriggeredInitialRefetch.current = false;
    }
  }, [isAuthenticated]);

  // Handle tab switching when user flow changes
  useEffect(() => {
    // If user is on 'videos' tab but switches to remote flow, switch to 'booking'
    if (userFlow === 'remote' && activeTab === 'videos') {
      setActiveTab('booking');
    }
    // If user is on 'meeting' tab but switches to in-person flow, switch to 'booking'
    if (userFlow === 'in-person' && activeTab === 'meeting') {
      setActiveTab('booking');
    }
  }, [userFlow, activeTab]);

  // Handle router state for active tab
  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
      // Clear the state to prevent it from persisting on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleInviteMore = () => {
    navigate("/invite-friends", { state: { fromDashboard: true } });
  };

  const handleFormNavigation = () => {
    navigate("/user-form");
  };

  // Handle tab navigation
  useEffect(() => {
    if (activeTab === 'form') {
      handleFormNavigation();
    } else if (activeTab === 'invite') {
      handleInviteMore();
    }
  }, [activeTab]);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetch();
    } catch (error) {
      console.error('Failed to refresh:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Extract user name from profile data (correct API response structure)
  const userName =
    userProfile?.data?.user?.name ||
    userProfile?.user?.name ||
    userProfile?.name ||
    "User";
  const userFirstName = userName.split(" ")[0]; // Get first name only

  // Show error state if profile fetch fails
  if (error) {
    console.error("Error fetching user profile:", error);
  }

  // Log specific slot data for debugging
  if (specificSlot) {
    console.log("Specific slot data:", specificSlot);
  }
  if (slotError) {
    console.error("Error fetching specific slot:", slotError);
  }

  // Extract the first slot from the API response
  const slotData = specificSlot?.slots?.[0];

  // Format date for display
  const formatDate = (dateString) => {
    return formatDateForDisplay(dateString);
  };

  // Updated formatTime function to handle UTC to local conversion
  const formatTime = (utcTimeString, dateString) => {
    return convertUTCToLocalTime(utcTimeString, dateString);
  };

  // Get platform display name
  const getPlatformName = (platform) => {
    switch (platform) {
      case "google":
        return "Google Meet";
      case "microsoft": 
        return "Microsoft Teams";
      case "zoom":
        return "Zoom";
      default:
        return "TBD";
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-foreground mb-4">
            My Legacy Dashboard
          </h1>
          <p className="text-xl text-muted-foreground">
            Welcome back, {isLoading ? "Loading..." : userFirstName}! 
            {userFlow === 'remote' ? ' Manage your remote session.' : ' Manage your legacy story and videos.'}
          </p>
        </div>

        {/* Tab Navigation - Desktop Only */}
        <div className="mb-6 hidden md:block">
          <div className="flex space-x-1 bg-secondary border border-secondary-border rounded-lg p-1">
            <button
              onClick={() => setActiveTab('booking')}
              className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'booking'
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
              }`}
            >
              <Calendar className="h-4 w-4 mr-2" />
              {userFlow === 'remote' ? 'Session Details' : 'Booking Details'}
            </button>
            
            {/* Conditional tab based on user flow - only show videos tab for in-person */}
            {userFlow === 'in-person' && (
              <button
                onClick={() => setActiveTab('videos')}
                className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'videos'
                    ? 'bg-primary text-white shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
                }`}
              >
                <Video className="h-4 w-4 mr-2" />
                My Videos
              </button>
            )}
            
            <button
              onClick={handleFormNavigation}
              className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors text-muted-foreground hover:text-foreground hover:bg-secondary/50`}
            >
              <FileText className="h-4 w-4 mr-2" />
              My Form
            </button>
            <button
              onClick={handleInviteMore}
              className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors text-muted-foreground hover:text-foreground hover:bg-secondary/50`}
            >
              <Plus className="h-4 w-4 mr-2" />
              Invite Friends
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'booking' && (
        <div className="bg-secondary border border-secondary-border rounded-xl p-8">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-[1.25rem] font-semibold text-foreground">
              {userFlow === 'remote' ? 'Your Session Details' : 'Your Booking Details'}
            </h3>
            {userFlow === 'remote' && (
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="inline-flex items-center px-3 py-2 text-sm bg-secondary border border-secondary-border rounded-md hover:bg-background transition-colors disabled:opacity-50"
                title="Refresh session details"
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
                {isRefreshing ? 'Refreshing...' : 'Refresh'}
              </button>
            )}
          </div>

          <SessionDetailsTab 
            userFlow={userFlow}
            slotData={slotData}
            isSlotLoading={isSlotLoading}
            slotError={slotError}
            isRefreshing={isRefreshing}
            handleRefresh={handleRefresh}
            formatDate={formatDate}
            formatTime={formatTime}
            getPlatformName={getPlatformName}
            copiedLink={copiedLink}
            copyToClipboard={copyToClipboard}
          />
        </div>
        )}

        {activeTab === 'videos' && userFlow === 'in-person' && (
          <div className="bg-secondary border border-secondary-border rounded-xl p-6">
            <UserVideos />
          </div>
        )}
      </div>
    </div>
  );
} 